import { CAPABILITIES, type Capability } from '@/util/roles/capabilities'
import {
  ALL_ROLES,
  OPERATION_ROLES,
  type Operation,
} from '@/util/roles/generated'

/**
 * True when `userRoles` includes one of the roles required by `target` — either a
 * named capability or a single operation. A capability passes only if every
 * operation it groups passes.
 *
 * An empty required-roles list mirrors the spec's "any role may invoke this".
 *
 * Pure: no store or feature-flag access. Callers that should respect the
 * `rbacEnforced` flag go through `useAuthStore().can()` instead.
 */
export function can(
  target: Capability | Operation,
  userRoles: readonly string[],
): boolean {
  const ops: readonly Operation[] =
    target in CAPABILITIES
      ? CAPABILITIES[target as Capability]
      : [target as Operation]

  return ops.every((op) => {
    const required = OPERATION_ROLES[op]
    const admitted = required.length === 0 ? ALL_ROLES : required
    return admitted.some((role) => userRoles.includes(role))
  })
}

/**
 * True when the user may invoke at least one guarded operation. Only mutating
 * operations carry required roles right now, so that means writes are allowed
 * if the user holds any role specified (admin or user).
 */
export function canMutate(userRoles: readonly string[]): boolean {
  return Object.values(OPERATION_ROLES).some(
    (required) =>
      required.length > 0 && required.some((role) => userRoles.includes(role)),
  )
}
